import { useEffect, useMemo, useState } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Checkbox,
  Search,
} from '@carbon/react';
import { Close } from '@carbon/react/icons';
import {
  COLOR_BY_ATTRIBUTES,
  buildAttrValueOptions,
  colorForValue,
  buildColorRule,
  type ColorByAttrKey,
  type ColorRule,
  type ColorRuleValue,
} from './color-by';
import { format0 } from 'utils/number';
import type { ComparisonResult } from './types';
import style from './attribute-modal.module.scss';

// Deliberately NOT the shared components/color-by-attribute-modal. That generic
// modal models a rule as parallel selectedValues/selectedValueLabels arrays + a
// valueColors map + a consumer-defined applyTo Record, and its onApply returns
// the whole rule stack — a shape built to survive the workspace serializer's
// key-camelization. This feature persists rules in a jotai atom (no serializer)
// and uses the local ColorRule shape from ./color-by: a single combined
// values[] array, named applyToMap/applyToTables booleans, and a typed attribute
// union, with onApply returning one rule. Reconciling the two would mean an
// adapter layer heavier than this component; kept feature-local on purpose.
interface ComparisonColorByModalProps {
  open: boolean;
  onClose: () => void;
  results: ComparisonResult[];
  /** When set, the modal edits this rule in place; otherwise it creates one. */
  editingRule?: ColorRule | null;
  onApply: (rule: ColorRule) => void;
}

/**
 * "Color by Attribute" modal (mock v16): pick an attribute, choose which of its
 * values to color, and choose the surfaces (map / property tables) the color-by
 * applies to. Applying adds (or updates) an ordered color rule in the sidebar.
 * @param {ComparisonColorByModalProps} props - open state, results, edit target, handlers
 * @return {JSX.Element} The color-by modal
 */
export const ComparisonColorByModal = ({
  open,
  onClose,
  results,
  editingRule = null,
  onApply,
}: ComparisonColorByModalProps) => {
  const [attribute, setAttribute] = useState<ColorByAttrKey>('originVsTarget');
  const [attrSearch, setAttrSearch] = useState('');
  const [valueSearch, setValueSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [applyToMap, setApplyToMap] = useState(true);
  const [applyToTables, setApplyToTables] = useState(true);

  // Sync the form to the target rule (or defaults) each time the modal opens.
  useEffect(() => {
    if (!open) return;
    if (editingRule) {
      setAttribute(editingRule.attribute);
      setSelected(new Set(editingRule.values.map((v) => v.value)));
      setApplyToMap(editingRule.applyToMap);
      setApplyToTables(editingRule.applyToTables);
    } else {
      setAttribute('originVsTarget');
      setSelected(new Set());
      setApplyToMap(true);
      setApplyToTables(true);
    }
    setAttrSearch('');
    setValueSearch('');
  }, [open, editingRule]);

  const attributes = useMemo(
    () =>
      COLOR_BY_ATTRIBUTES.filter((a) =>
        a.label.toLowerCase().includes(attrSearch.trim().toLowerCase())
      ),
    [attrSearch]
  );

  const attrDef = COLOR_BY_ATTRIBUTES.find((a) => a.key === attribute);

  const allOptions = useMemo(
    () => buildAttrValueOptions(results, attribute),
    [results, attribute]
  );

  const visibleOptions = useMemo(() => {
    const q = valueSearch.trim().toLowerCase();
    return q
      ? allOptions.filter((o) => o.label.toLowerCase().includes(q))
      : allOptions;
  }, [allOptions, valueSearch]);

  // Chips reflect the selected values in the attribute's canonical order.
  const selectedOptions = allOptions.filter((o) => selected.has(o.value));

  const pickAttribute = (key: ColorByAttrKey) => {
    setAttribute(key);
    setSelected(new Set());
    setValueSearch('');
  };

  const toggleValue = (value: string, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(value);
      else next.delete(value);
      return next;
    });

  const selectAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      visibleOptions.forEach((o) => next.add(o.value));
      return next;
    });

  const clearValues = () => setSelected(new Set());

  const handleApply = () => {
    const values: ColorRuleValue[] = selectedOptions.map((option, index) => ({
      value: option.value,
      label: option.label,
      color:
        editingRule?.values.find((v) => v.value === option.value)?.color ??
        colorForValue(attribute, option.value, index),
    }));
    onApply(
      buildColorRule({
        id: editingRule?.id,
        attribute,
        values,
        applyToMap,
        applyToTables,
        enabled: editingRule?.enabled ?? true,
      })
    );
    onClose();
  };

  return (
    <ComposedModal open={open} onClose={onClose} size="lg">
      <ModalHeader
        title="Color by Attribute"
        label={editingRule ? 'Edit color rule' : undefined}
      />
      <ModalBody hasScrollingContent>
        <div className={style.body}>
          <div className={style.listCol}>
            <Search
              size="sm"
              labelText="Search Attributes"
              placeholder="Search Attributes"
              value={attrSearch}
              onChange={(e) => setAttrSearch(e.target.value)}
            />
            <ul className={style.list}>
              {attributes.map((a) => (
                <li key={a.key}>
                  <button
                    type="button"
                    className={`${style.listItem} ${
                      a.key === attribute ? style.listItemActive : ''
                    }`}
                    onClick={() => pickAttribute(a.key)}
                  >
                    {a.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className={style.detailCol}>
            <h3 className={style.detailTitle}>{attrDef?.label}</h3>
            <p className={style.detailDesc}>
              Select the values to color and choose where it applies.
            </p>

            <p className={style.selectedLabel}>Selected values</p>
            {selectedOptions.length === 0 ? (
              <p className={style.emptyValues}>No values selected</p>
            ) : (
              <div className={style.chips}>
                {selectedOptions.map((o) => (
                  <span key={o.value} className={style.chip}>
                    {o.label}
                    <button
                      type="button"
                      className={style.chipRemove}
                      aria-label={`Remove ${o.label}`}
                      onClick={() => toggleValue(o.value, false)}
                    >
                      <Close size={16} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <hr className={style.divider} />

            <Search
              size="sm"
              labelText={`Search ${attrDef?.label ?? ''}`}
              placeholder={`Search ${attrDef?.label.toLowerCase() ?? ''}...`}
              value={valueSearch}
              onChange={(e) => setValueSearch(e.target.value)}
            />
            <div className={style.valuesHeader}>
              <Button kind="ghost" size="sm" onClick={selectAllVisible}>
                Select All
              </Button>
              <span className={style.resultsHint}>
                {format0(visibleOptions.length)} results shown
              </span>
            </div>
            <div className={style.valueList}>
              {visibleOptions.map((o) => (
                <Checkbox
                  key={o.value}
                  id={`color-value-${attribute}-${o.value}`}
                  labelText={o.label}
                  checked={selected.has(o.value)}
                  onChange={(_evt, { checked }: { checked: boolean }) =>
                    toggleValue(o.value, checked)
                  }
                />
              ))}
            </div>
            <div className={style.clearRow}>
              <Button kind="ghost" size="sm" onClick={clearValues}>
                Clear
              </Button>
            </div>
          </div>

          <div className={style.applyCol}>
            <p className={style.applyTitle}>APPLY TO</p>
            <p className={style.applyDesc}>
              Choose which surfaces this color-by colors.
            </p>
            <Checkbox
              id="color-apply-map"
              labelText="Map"
              checked={applyToMap}
              onChange={(_evt, { checked }: { checked: boolean }) =>
                setApplyToMap(checked)
              }
            />
            <Checkbox
              id="color-apply-tables"
              labelText="Property tables"
              checked={applyToTables}
              onChange={(_evt, { checked }: { checked: boolean }) =>
                setApplyToTables(checked)
              }
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={handleApply}
          disabled={selectedOptions.length === 0}
        >
          Apply
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};
